import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Quiz, QuizAttempt, QuizAnswer, QuizQuestion } from '@/types/quiz';

export const usePlayerQuizzes = (programId?: string, playerId?: string) => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchQuizzes = useCallback(async () => {
    if (!programId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('quizzes')
        .select('*')
        .eq('program_id', programId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setQuizzes(data || []);
    } catch (error) {
      console.error('Error fetching player quizzes:', error);
      toast.error('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    fetchQuizzes();
  }, [fetchQuizzes]);

  return {
    quizzes,
    loading,
    refetch: fetchQuizzes
  };
};

export const useQuizAttempt = (playerId?: string) => {
  const [currentAttempt, setCurrentAttempt] = useState<QuizAttempt | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const startQuizAttempt = async (quizId: string) => {
    if (!playerId) {
      toast.error('Player not found');
      return null;
    }

    try {
      setLoading(true);

      // Get existing attempts count
      const { data: existingAttempts, error: attemptsError } = await supabase
        .from('quiz_attempts')
        .select('attempt_number')
        .eq('player_id', playerId)
        .eq('quiz_id', quizId)
        .order('attempt_number', { ascending: false })
        .limit(1);

      if (attemptsError) throw attemptsError;

      const nextAttemptNumber = (existingAttempts?.[0]?.attempt_number || 0) + 1;

      // Fetch quiz questions
      const { data: questionsData, error: questionsError } = await supabase
        .from('quiz_questions')
        .select(`
          *,
          options:quiz_question_options(*)
        `)
        .eq('quiz_id', quizId)
        .order('order_index', { ascending: true });

      if (questionsError) throw questionsError;

      const maxPossiblePoints = questionsData?.reduce((sum, q) => sum + q.points_value, 0) || 0;

      // Create new attempt
      const { data: attempt, error: attemptError } = await supabase
        .from('quiz_attempts')
        .insert([{
          player_id: playerId,
          quiz_id: quizId,
          attempt_number: nextAttemptNumber,
          max_possible_points: maxPossiblePoints,
          is_completed: false
        }])
        .select()
        .single();

      if (attemptError) throw attemptError;

      setCurrentAttempt(attempt);
      setQuestions(questionsData || []);
      setAnswers({});
      
      return attempt;
    } catch (error) {
      console.error('Error starting quiz attempt:', error);
      toast.error('Failed to start quiz');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const saveAnswer = async (questionId: string, answer: string, optionId?: string) => {
    if (!currentAttempt) return;

    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    let isCorrect = false;
    let pointsEarned = 0;

    // Determine if answer is correct based on question type
    if (question.question_type === 'multiple_choice') {
      const selectedOption = question.options?.find(opt => opt.id === optionId);
      isCorrect = selectedOption?.is_correct || false;
    } else if (question.question_type === 'true_false') {
      const correctOption = question.options?.find(opt => opt.is_correct);
      isCorrect = answer.toLowerCase() === correctOption?.option_text?.toLowerCase();
    } else if (question.question_type === 'fill_in_blank') {
      const correctAnswer = question.options?.[0]?.option_text?.toLowerCase();
      isCorrect = answer.toLowerCase().trim() === correctAnswer;
    }

    if (isCorrect) {
      pointsEarned = question.points_value;
    }

    try {
      // Save or update the answer
      const { error } = await supabase
        .from('quiz_answers')
        .upsert([{
          attempt_id: currentAttempt.id,
          question_id: questionId,
          selected_option_id: optionId,
          answer_text: answer,
          is_correct: isCorrect,
          points_earned: pointsEarned
        }], {
          onConflict: 'attempt_id,question_id'
        });

      if (error) throw error;

      // Update local state - store option ID for multiple choice, text for others
      setAnswers(prev => ({
        ...prev,
        [questionId]: question.question_type === 'multiple_choice' ? (optionId || answer) : answer
      }));

    } catch (error) {
      console.error('Error saving answer:', error);
      toast.error('Failed to save answer');
    }
  };

  const submitQuiz = async () => {
    if (!currentAttempt) {
      toast.error('No active quiz attempt');
      return null;
    }

    try {
      setLoading(true);

      // Calculate final score
      const { data: quizAnswers, error: answersError } = await supabase
        .from('quiz_answers')
        .select('points_earned')
        .eq('attempt_id', currentAttempt.id);

      if (answersError) throw answersError;

      const totalPoints = quizAnswers?.reduce((sum, answer) => sum + answer.points_earned, 0) || 0;
      const score = Math.round((totalPoints / currentAttempt.max_possible_points) * 100);

      // Update attempt as completed
      const { data: completedAttempt, error: updateError } = await supabase
        .from('quiz_attempts')
        .update({
          is_completed: true,
          completed_at: new Date().toISOString(),
          score: score,
          total_points: totalPoints
        })
        .eq('id', currentAttempt.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Award points to player
      await awardPointsToPlayer(playerId!, totalPoints, currentAttempt.quiz_id);

      setCurrentAttempt(completedAttempt);
      toast.success(`Quiz completed! You scored ${score}% and earned ${totalPoints} points!`);
      
      return completedAttempt;
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const awardPointsToPlayer = async (playerId: string, points: number, quizId: string) => {
    try {
      // Create point transaction
      await supabase
        .from('point_transactions')
        .insert([{
          player_id: playerId,
          amount: points,
          transaction_type: 'earned',
          source_type: 'quiz',
          source_id: quizId,
          description: 'Quiz completion reward'
        }]);

      // Update player points balance
      const { data: playerPoints } = await supabase
        .from('player_points')
        .select('*')
        .eq('player_id', playerId)
        .single();

      if (playerPoints) {
        await supabase
          .from('player_points')
          .update({
            current_balance: (playerPoints.current_balance || 0) + points,
            total_earned: (playerPoints.total_earned || 0) + points
          })
          .eq('player_id', playerId);
      } else {
        await supabase
          .from('player_points')
          .insert([{
            player_id: playerId,
            current_balance: points,
            total_earned: points
          }]);
      }
    } catch (error) {
      console.error('Error awarding points:', error);
    }
  };

  return {
    currentAttempt,
    questions,
    answers,
    loading,
    startQuizAttempt,
    saveAnswer,
    submitQuiz
  };
};

export const useQuizAttemptHistory = (playerId?: string, quizId?: string) => {
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAttempts = useCallback(async () => {
    if (!playerId || !quizId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('quiz_attempts')
        .select(`
          *,
          quiz:quizzes(*)
        `)
        .eq('player_id', playerId)
        .eq('quiz_id', quizId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAttempts(data || []);
    } catch (error) {
      console.error('Error fetching quiz attempts:', error);
      toast.error('Failed to load quiz history');
    } finally {
      setLoading(false);
    }
  }, [playerId, quizId]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  return {
    attempts,
    loading,
    refetch: fetchAttempts
  };
};